import { useCallback, useMemo } from "react";

import { stringifyUrl } from "query-string";

import { messageSales } from "@/api/endpoints/contact";
import { createStripeCheckoutSession, getPlans } from "@/api/endpoints/stripe";
import { PLAN_TYPE, PlanType } from "@/constants";
import { useMessages } from "@/containers/Messages/MessageContext";
import { useI18n } from "@/contexts/i18nContext";
import { useOrganization } from "@/hooks/useOrganization";
import { useToggle } from "@/hooks/useToggle";
import { useUser } from "@/hooks/useUser";
import { basic, custom, free } from "@/mappings/plans";
import { ROUTES } from "@/routes_new";
import { User } from "@/types/User";
import { cx } from "@/utils";
import Button, { ButtonProps } from "@components/Button";
import Modal, { ModalContent } from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";
import * as Sentry from "@sentry/browser";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import ContactSalesModal from "../ContactSalesModal";
import * as PlanCard from "./PlanCard";

import styles from "./PlanModal.module.css";

export type PlanModalProps = {
  className?: string;
};

export const usePlanData = () => {
  return useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
  });
};

export const Cta = (props: ButtonProps) => (
  <Button className={styles.cta} {...props} />
);

export const ContactSalesCta = ({
  onSent,
  ...props
}: ButtonProps & { onSent?: () => void }) => {
  const { data: user } = useUser();
  const { name: full_name = "", email = "" } = (user?.data as User) || {};

  const { danger } = useMessages();
  const [modalOpen, toggleModalOpen] = useToggle(false);

  const contactSales = async () => {
    return messageSales({ name: full_name, email, type: "upsale" }).then(
      (res) => {
        if (res.ok) {
          toggleModalOpen();
          onSent?.();
        } else {
          danger({
            title: L.CONTACT_SALES_MESSAGE_ERROR,
          });
        }
      }
    );
  };

  return (
    <>
      <ContactSalesModal show={modalOpen} onHide={contactSales} />
      <Cta {...props} onClick={toggleModalOpen} />
    </>
  );
};

export const CurrentBadge = () => {
  const { type } = PlanCard.usePlanCard();
  const { data: orgData } = useOrganization();
  const { plan = null } = orgData?.ok ? orgData.data : {};

  if (plan?.type !== type) return null;

  return (
    <span className={cx(styles.banner, styles.currentPlan)}>
      {L.UPSALE_CURRENT_OPTION}
    </span>
  );
};

export const useStripePlanUpgrade = (planType: PlanType) => {
  const { currency } = useI18n();
  const { isLoading, data } = usePlanData();

  const plan = useMemo(
    () => (data?.ok ? data.data : []).find((plan) => plan.type === planType),
    [planType, data]
  );

  const upgrade = useCallback(() => {
    if (!plan) return;
    const { stripe_default_price_ids } = plan;

    const price_id =
      stripe_default_price_ids?.[currency.toLowerCase()] ??
      stripe_default_price_ids?.["sek"];

    createStripeCheckoutSession(price_id, plan.id)
      .then((response) => {
        if (response.data && response.ok) {
          window.location.href = response.data.url;
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  }, [plan, currency]);

  return { isLoading, upgrade, plan, disabled: plan === undefined };
};

const PlanModal = ({ className, ...props }: ModalProps) => {
  const { data: user } = useUser();
  const { email = "" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { plan: currentPlan = null } = orgData?.ok ? orgData.data : {};

  const { isLoading, upgrade, disabled } = useStripePlanUpgrade(
    PLAN_TYPE.BASIC
  );
  const stripeLink = stringifyUrl({
    url: ROUTES.BILLING_PORTAL,
    query: { prefilled_email: email },
  });

  return (
    <>
      <Modal
        className={cx(styles.root, className)}
        contentLoading={isLoading}
        data-testid="plan-modal"
        {...props}
      >
        <ModalContent className={styles.content}>
          <div className={styles.plans}>
            {/** FREE */}
            <PlanCard.Root plan={free}>
              <div className={styles.card}>
                <header>
                  <PlanCard.Price />
                  <PlanCard.Legal />
                </header>
                <PlanCard.Name>
                  <CurrentBadge />
                </PlanCard.Name>
                <PlanCard.Description />
                <PlanCard.FeatureTitle />
                <PlanCard.Features />
                {currentPlan?.type === PLAN_TYPE.FREE && (
                  <Cta variant="secondary" onClick={props?.onHide}>
                    {L.UPSALE_FREE_CONTINUE_CTA}
                  </Cta>
                )}
              </div>
            </PlanCard.Root>

            {/** BASIC */}
            <PlanCard.Root plan={basic}>
              <div className={cx(styles.card, styles.basic)}>
                <span className={cx(styles.banner, styles.popularPlan)}>
                  {L.UPSALE_POPULAR_OPTION}
                </span>
                <header>
                  <PlanCard.Price />
                  <PlanCard.Legal />
                </header>
                <PlanCard.Name>
                  <CurrentBadge />
                </PlanCard.Name>
                <div>
                  <PlanCard.MonthlyPrice />
                  <PlanCard.Description />
                </div>
                <PlanCard.FeatureTitle />
                <PlanCard.Features />
                {currentPlan?.type ===
                PLAN_TYPE.CUSTOM ? null : currentPlan?.type ===
                  PLAN_TYPE.FREE ? (
                  <Cta
                    disabled={disabled}
                    onClick={() => upgrade()}
                    data-testid="upgrade-to-basic"
                  >
                    {L.UPSALE_BASIC_CTA}
                  </Cta>
                ) : (
                  <Cta onClick={props?.onHide}>
                    {L.UPSALE_BASIC_CONTINUE_CTA}
                  </Cta>
                )}
              </div>
            </PlanCard.Root>

            {/** CUSTOM */}
            <PlanCard.Root plan={custom}>
              <div className={styles.card}>
                <header>
                  <PlanCard.Price />
                  <PlanCard.Legal />
                </header>
                <PlanCard.Name>
                  <CurrentBadge />
                </PlanCard.Name>
                <PlanCard.Description />
                <PlanCard.FeatureTitle />
                <PlanCard.Features />
                {currentPlan?.type === PLAN_TYPE.CUSTOM ? (
                  <Cta href={stripeLink}>{L.UPSALE_CUSTOM_CONTINUE_CTA}</Cta>
                ) : (
                  <ContactSalesCta onSent={props?.onHide}>
                    {L.UPSALE_CUSTOM_CTA}
                  </ContactSalesCta>
                )}
              </div>
            </PlanCard.Root>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PlanModal;
